/**
 * Web push / browser notifications for newly curated trends.
 *
 * Permission + the service-worker registration live here; the subscription is
 * mirrored into `trend_push_subscriptions` so alerts can also be delivered when
 * the dashboard is closed.
 */
import { supabase } from "@/integrations/supabase/client";
import { trendDeepLinkUrl } from "@/lib/trendNotifications";

export const TREND_PUSH_SW_PATH = "/sw-trend-push.js";

const VAPID_PUBLIC_KEY: string | undefined = (
  import.meta.env.VITE_TREND_PUSH_VAPID_PUBLIC_KEY as string | undefined
)?.trim() || undefined;

export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator
  );
}

export function pushPermission(): NotificationPermission | "unsupported" {
  if (!pushSupported()) return "unsupported";
  return Notification.permission;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  try {
    return await navigator.serviceWorker.register(TREND_PUSH_SW_PATH);
  } catch {
    return null;
  }
}

/**
 * Asks for notification permission and (when a VAPID key is configured)
 * registers a push subscription for this device. Resolves to false when the
 * user declines or the browser can't do push.
 */
export async function enableTrendPush(): Promise<boolean> {
  if (!pushSupported()) return false;
  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") return false;

  const reg = await registration();
  if (!reg) return true; // permission granted: in-page notifications still work

  if (!VAPID_PUBLIC_KEY) return true;
  try {
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      }));
    const json = sub.toJSON() as { endpoint?: string; keys?: Record<string, string> };
    const { data: auth } = await supabase.auth.getUser();
    if (auth.user && json.endpoint) {
      await supabase.from("trend_push_subscriptions").upsert(
        {
          user_id: auth.user.id,
          endpoint: json.endpoint,
          p256dh: json.keys?.p256dh ?? null,
          auth: json.keys?.auth ?? null,
          user_agent: navigator.userAgent.slice(0, 300),
        },
        { onConflict: "endpoint" },
      );
    }
  } catch {
    /* push subscribe is best-effort — local notifications still work */
  }
  return true;
}

/** Removes this device's push subscription (permission itself is browser-owned). */
export async function disableTrendPush(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(TREND_PUSH_SW_PATH);
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await supabase.from("trend_push_subscriptions").delete().eq("endpoint", endpoint);
    }
  } catch {
    /* noop */
  }
}

/** Shows a notification for newly curated trends outside the dashboard UI. */
export async function showTrendPushNotification(
  title: string,
  body: string,
  trendId: string,
): Promise<void> {
  if (!pushSupported() || Notification.permission !== "granted") return;
  const url = trendDeepLinkUrl(trendId);
  try {
    const reg = await navigator.serviceWorker.getRegistration(TREND_PUSH_SW_PATH);
    if (reg) {
      await reg.showNotification(title, {
        body,
        tag: `td-trend-${trendId}`,
        data: { url },
      });
      return;
    }
    new Notification(title, { body, tag: `td-trend-${trendId}` });
  } catch {
    /* noop */
  }
}
